import { io } from "socket.io-client";

/**
 * Broker WebSocket Service - ULTIMATE BULLETPROOF VERSION
 * 
 * 5-LAYER RECONNECTION PROTECTION:
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * Layer 1: Ping/Pong Monitoring      → Detects server/network death (35s)
 * Layer 2: Data Flow Heartbeat       → Detects frozen WebSocket (15s)
 * Layer 3: Page Visibility API       → Detects browser sleep/wake
 * Layer 4: Transport Monitoring      → Detects connection degradation
 * Layer 5: Exponential Backoff       → Smart retry with jitter
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * 
 * Real-time monitoring for ALL accounts under the brokerage:
 * - account_update, position events, orders, margin alerts, broker stats
 *
 * ZERO SERVER CHANGES REQUIRED - Works with existing infrastructure
 */
class BrokerWebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.listeners = new Map();
    this.subscribedSymbols = new Set();

    // Reconnection management
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.baseReconnectDelay = 1000;
    this.maxReconnectDelay = 30000;
    
    // ============================================================================
    // LAYER 1: PING/PONG MONITORING (Server sends ping every 25s)
    // ============================================================================
    this.lastPingReceived = Date.now();
    this.pingTimeout = 35000; // 35s (25s interval + 10s grace)
    
    // ============================================================================
    // LAYER 2: DATA FLOW HEARTBEAT (Any WebSocket event)
    // ============================================================================
    this.lastHeartbeat = Date.now();
    this.heartbeatTimeout = 15000; // 15s without data = reconnect
    this.heartbeatInterval = null;
    
    // ============================================================================
    // LAYER 5: EXPONENTIAL BACKOFF
    // ============================================================================
    this.isReconnecting = false;
    this.token = null; // Store token for reconnection
    
    // ============================================================================
    // CONNECTION QUALITY TRACKING
    // ============================================================================
    this.currentTransport = null;
    this.transportDowngrades = 0;
    this.connectionQuality = 'unknown'; // excellent | good | poor | degraded
    
    // Setup Layer 3: Page Visibility
    this.setupVisibilityListener();
  }

  // ============================================================================
  // LAYER 3: PAGE VISIBILITY API
  // ============================================================================
  setupVisibilityListener() {
    if (typeof document === 'undefined') return;
    
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ Admin tab became visible - running health check...');
        this.handleTabVisible();
      } else {
        console.log('🌙 Admin tab hidden - WebSocket may freeze');
      }
    });
    
    window.addEventListener('focus', () => {
      console.log('🎯 Admin window focused - running health check...');
      this.handleTabVisible();
    });
  }

  handleTabVisible() {
    // Multi-signal health check
    const timeSinceLastPing = Date.now() - this.lastPingReceived;
    const timeSinceLastData = Date.now() - this.lastHeartbeat;
    
    // Signal 1: No ping from server (network/server dead)
    if (timeSinceLastPing > this.pingTimeout) {
      console.warn(`⚠️ Admin: No server ping for ${timeSinceLastPing}ms - forcing reconnect`);
      this.forceReconnect('no_ping');
      return;
    }
    
    // Signal 2: No data but subscribed (WebSocket frozen)
    if (this.subscribedSymbols.size > 0 && timeSinceLastData > this.heartbeatTimeout) {
      console.warn(`⚠️ Admin: No data for ${timeSinceLastData}ms - forcing reconnect`);
      this.forceReconnect('no_data');
      return;
    }
    
    // Signal 3: Socket disconnected while hidden
    if (!this.socket?.connected) {
      console.warn('⚠️ Admin: Socket disconnected while tab was hidden - reconnecting');
      this.forceReconnect('disconnected');
      return;
    }
    
    console.log('✅ Admin WebSocket health check passed', {
      timeSinceLastPing: `${timeSinceLastPing}ms`,
      timeSinceLastData: `${timeSinceLastData}ms`,
      transport: this.currentTransport,
      quality: this.connectionQuality,
    });
  }

  // ============================================================================
  // LAYER 2: HEARTBEAT MONITORING
  // ============================================================================
  startHeartbeatMonitor() {
    this.stopHeartbeatMonitor();
    
    this.heartbeatInterval = setInterval(() => {
      const timeSinceLastPing = Date.now() - this.lastPingReceived;
      const timeSinceLastData = Date.now() - this.lastHeartbeat;
      
      // Critical: No ping from server (highest priority)
      if (timeSinceLastPing > this.pingTimeout && this.socket?.connected) {
        console.error(`💀 Admin: No server ping for ${timeSinceLastPing}ms - server/network issue`);
        this.forceReconnect('ping_timeout');
        return;
      }
      
      // Warning: No data flow (but server is alive)
      if (this.subscribedSymbols.size > 0 && 
          timeSinceLastData > this.heartbeatTimeout && 
          this.socket?.connected) {
        console.warn(`💀 Admin: No data for ${timeSinceLastData}ms - WebSocket frozen`);
        this.forceReconnect('data_timeout');
        return;
      }
      
      // Update connection quality based on timing
      this.updateConnectionQuality(timeSinceLastPing, timeSinceLastData);
      
    }, 5000); // Check every 5 seconds
  }

  stopHeartbeatMonitor() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  updateHeartbeat() {
    this.lastHeartbeat = Date.now();
  }

  updatePingReceived() {
    this.lastPingReceived = Date.now();
  }

  // ============================================================================
  // CONNECTION QUALITY ASSESSMENT
  // ============================================================================
  updateConnectionQuality(timeSinceLastPing, timeSinceLastData) {
    if (timeSinceLastPing < 5000 && timeSinceLastData < 2000) {
      this.connectionQuality = 'excellent';
    } else if (timeSinceLastPing < 15000 && timeSinceLastData < 5000) {
      this.connectionQuality = 'good';
    } else if (timeSinceLastPing < 25000 && timeSinceLastData < 10000) {
      this.connectionQuality = 'poor';
    } else {
      this.connectionQuality = 'degraded';
    }
  }

  // ============================================================================
  // LAYER 5: EXPONENTIAL BACKOFF WITH JITTER
  // ============================================================================
  calculateReconnectDelay() {
    // Exponential: 1s, 2s, 4s, 8s, 16s, 32s... capped at 30s
    const exponentialDelay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay
    );
    
    // Add jitter (0-1000ms) to prevent thundering herd
    const jitter = Math.random() * 1000;
    
    return exponentialDelay + jitter;
  }

  forceReconnect(reason = 'unknown') {
    if (this.isReconnecting) {
      console.log('⏳ Admin reconnection already in progress...');
      return;
    }
    
    this.isReconnecting = true;
    console.log(`🔄 Force reconnecting Admin WebSocket (reason: ${reason})...`);
    
    // Close old socket completely
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    
    this.isConnected = false;
    this.emit('connected', false);
    
    // Calculate backoff delay
    const delay = this.calculateReconnectDelay();
    console.log(`⏱️ Admin reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);
    
    // Reconnect after delay
    setTimeout(() => {
      if (this.token) {
        this.reconnectAttempts++;
        console.log('🔌 Reconnecting Admin with stored token...');
        this.isReconnecting = false;
        this.connect(this.token);
      } else {
        console.error('❌ Cannot reconnect Admin: No token stored');
        this.isReconnecting = false;
      }
    }, delay);
  }

  // ============================================================================
  // CONNECTION
  // ============================================================================
  connect(token) {
    // Store token for reconnection
    this.token = token;
    
    if (this.socket?.connected) {
      console.log('🔌 Broker Admin already connected to WebSocket');
      return;
    }

    const wsOrigin = window.location.origin;
    const wsPath = import.meta.env.VITE_WS_PATH || "/socket.io";

    console.log(`🔌 Broker Admin connecting to WebSocket... ${wsOrigin} (path: ${wsPath})`);
    console.log('🔑 Token present:', !!token);

    this.socket = io(wsOrigin, {
      path: wsPath,
      auth: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      timeout: 20000,
      pingTimeout: 60000,
      pingInterval: 25000,
    });

    // ============================================================================
    // LAYER 1: LISTEN TO SERVER PING/PONG
    // ============================================================================
    this.socket.io.on("ping", () => {
      console.log('🏓 Admin: Ping received from server');
      this.updatePingReceived();
      this.updateHeartbeat(); // Ping counts as activity too
    });

    this.socket.io.on("pong", (latency) => {
      console.log(`🏓 Admin: Pong - latency: ${latency}ms`);
    });

    // ============================================================================
    // LAYER 4: TRANSPORT MONITORING
    // ============================================================================
    this.socket.io.engine.on("upgrade", () => {
      const transportName = this.socket.io.engine.transport.name;
      console.log(`✅ Admin connection upgraded to ${transportName}`);
      this.currentTransport = transportName;
      this.transportDowngrades = 0;
    });

    this.socket.io.engine.on("packet", (packet) => {
      const currentTransportName = this.socket.io.engine.transport.name;
      if (this.currentTransport && this.currentTransport !== currentTransportName) {
        if (currentTransportName === 'polling' && this.currentTransport === 'websocket') {
          console.warn('⚠️ Admin: Downgraded from WebSocket to Polling - connection quality degraded');
          this.transportDowngrades++;
          this.connectionQuality = 'degraded';
        }
        this.currentTransport = currentTransportName;
      }
    });

    // ============================================================================
    // SOCKET.IO CORE EVENTS
    // ============================================================================
    this.socket.on("connect", () => {
      console.log('✅ Broker Admin WebSocket connected - ID:', this.socket.id);
      console.log('🚀 Transport:', this.socket.io.engine.transport.name);
      
      this.isConnected = true;
      this.reconnectAttempts = 0; // Reset on successful connection
      this.currentTransport = this.socket.io.engine.transport.name;
      this.updateHeartbeat();
      this.updatePingReceived();
      this.startHeartbeatMonitor();

      // Resubscribe to symbols after reconnection
      if (this.subscribedSymbols.size > 0) {
        const symbols = Array.from(this.subscribedSymbols);
        console.log('🔄 Resubscribing to symbols:', symbols);
        this.socket.emit("subscribe:prices", { symbols });
      }

      this.emit("connected", true);
    });

    this.socket.on("disconnect", (reason) => {
      console.log('❌ Broker Admin WebSocket disconnected:', reason);
      this.isConnected = false;
      this.stopHeartbeatMonitor();
      this.emit("connected", false);
      
      // If disconnected while tab is visible, attempt reconnect
      if (document.visibilityState === 'visible' && 
          (reason === 'transport close' || reason === 'transport error')) {
        console.log('🔄 Unexpected disconnect while visible - will reconnect...');
        setTimeout(() => this.forceReconnect('unexpected_disconnect'), 2000);
      }
    });

    this.socket.on("connect_error", (error) => {
      console.error('❌ WebSocket connection error:', error.message);
      this.isConnected = false;
      this.emit("connected", false);
    });

    this.socket.on("error", (error) => {
      console.error('❌ WebSocket error:', error);
      this.updateHeartbeat();
      this.emit("error", error);
    });

    // Debug logging in dev
    if (import.meta.env.DEV) {
      this.socket.onAny((event, data) => {
        console.log('📨 Broker WS Event:', event, data);
      });
    }

    this.setupListeners();
  }

  setupListeners() {
    console.log('🎧 Setting up Broker WebSocket event listeners...');

    // Remove any existing listeners to prevent duplicates
    this.socket.off("positions:update");
    this.socket.off("position:opened");
    this.socket.off("position:closed");
    this.socket.off("position:modified");
    this.socket.off("position:sl_triggered");
    this.socket.off("position:tp_triggered");
    this.socket.off("account:update");
    this.socket.off("account_update");
    this.socket.off("account:balance");
    this.socket.off("margin:warning");
    this.socket.off("margin:stop_out");
    this.socket.off("account:margin_call");
    this.socket.off("account:liquidated");
    this.socket.off("order:placed");
    this.socket.off("order:cancelled");
    this.socket.off("order:modified");
    this.socket.off("order:filled");
    this.socket.off("price:update");
    this.socket.off("price:subscribed");
    this.socket.off("candle:update");
    this.socket.off("candle:subscribed");
    this.socket.off("broker:stats");
    this.socket.off("broker:exposure");
    this.socket.off("broker:alert");
    this.socket.off("client:login");
    this.socket.off("client:logout");
    this.socket.off("notification");
    this.socket.off("order:error");
    this.socket.off("position:error");
    this.socket.off("admin:error");

    // ============================================================================
    // POSITION EVENTS (ALL ACCOUNTS) - All update heartbeat
    // ============================================================================
    this.socket.on("positions:update", (data) => {
      console.log('📋 All positions received:', data);
      this.updateHeartbeat();
      this.emit("positions:update", data);
    });

    this.socket.on("position:opened", (data) => {
      console.log("🟢 Position opened:", {
        account: data.account_id,
        symbol: data.symbol,
        side: data.side,
        volume: data.volume
      });
      this.updateHeartbeat();
      this.emit("position:opened", data);
    });

    this.socket.on("position:closed", (data) => {
      console.log("🔴 Position closed:", {
        account: data.account_id,
        symbol: data.symbol,
        pnl: data.net_profit || data.pnl
      });
      this.updateHeartbeat();
      this.emit("position:closed", data);
    });

    this.socket.on("position:modified", (data) => {
      console.log("✏️ Position modified:", {
        account: data.account_id,
        position: data.position_id
      });
      this.updateHeartbeat();
      this.emit("position:modified", data);
    });

    this.socket.on("position:sl_triggered", (data) => {
      console.log("🎯 Stop Loss triggered:", {
        account: data.account_id,
        symbol: data.symbol,
        loss: data.pnl
      });
      this.updateHeartbeat();
      this.emit("position:sl_triggered", data);
    });

    this.socket.on("position:tp_triggered", (data) => {
      console.log("🎯 Take Profit triggered:", {
        account: data.account_id,
        symbol: data.symbol,
        profit: data.pnl
      });
      this.updateHeartbeat();
      this.emit("position:tp_triggered", data);
    });

    // ============================================================================
    // ORDER EVENTS (ALL ACCOUNTS) - All update heartbeat
    // ============================================================================
    this.socket.on("order:placed", (data) => {
      console.log("✅ Order placed:", {
        account: data.account_id,
        symbol: data.symbol,
        type: data.type
      });
      this.updateHeartbeat();
      this.emit("order:placed", data);
    });

    this.socket.on("order:cancelled", (data) => {
      console.log("❌ Order cancelled:", {
        account: data.account_id,
        order: data.order_id
      });
      this.updateHeartbeat();
      this.emit("order:cancelled", data);
    });

    this.socket.on("order:modified", (data) => {
      console.log("✏️ Order modified:", {
        account: data.account_id,
        order: data.order_id
      });
      this.updateHeartbeat();
      this.emit("order:modified", data);
    });

    this.socket.on("order:filled", (data) => {
      console.log("✅ Order filled:", {
        account: data.account_id,
        order: data.order_id
      });
      this.updateHeartbeat();
      this.emit("order:filled", data);
    });

    // ============================================================================
    // ACCOUNT EVENTS (ALL ACCOUNTS) - All update heartbeat
    // ============================================================================
    this.socket.on("account_snapshot", (snapshot) => {
      this.updateHeartbeat();
      this.emit("account_snapshot", snapshot);
    });
    
    this.socket.on("account_update", (snapshot) => {
      this.updateHeartbeat();
      this.emit("account_snapshot", snapshot);
    });

    this.socket.on("account:update", (data) => {
      console.log('💰 Account balance updated:', {
        account: data.account_id,
        balance: data.balance
      });
      this.updateHeartbeat();
      this.emit("account:update", data);
    });

    this.socket.on("account:balance", (data) => {
      console.log("💰 Balance update:", {
        account: data.account_id,
        balance: data.balance
      });
      this.updateHeartbeat();
      this.emit("account:balance", data);
    });

    // ============================================================================
    // MARGIN EVENTS (ALL ACCOUNTS) - All update heartbeat
    // ============================================================================
    this.socket.on("margin:warning", (data) => {
      console.warn("⚠️ MARGIN CALL:", {
        account: data.account_id,
        level: data.margin_level
      });
      this.updateHeartbeat();
      this.emit("margin:warning", data);
    });

    this.socket.on("margin:stop_out", (data) => {
      console.error("🚨 STOP OUT:", {
        account: data.account_id,
        level: data.margin_level,
        positions_closed: data.positions_closed?.length
      });
      this.updateHeartbeat();
      this.emit("margin:stop_out", data);
    });

    this.socket.on("account:margin_call", (data) => {
      console.warn("⚠️ MARGIN CALL (legacy):", {
        account: data.account_id,
        level: data.margin_level
      });
      this.updateHeartbeat();
      this.emit("account:margin_call", data);
    });

    this.socket.on("account:liquidated", (data) => {
      console.error("🚨 ACCOUNT LIQUIDATED:", data.account_id);
      this.updateHeartbeat();
      this.emit("account:liquidated", data);
    });

    // ============================================================================
    // PRICE EVENTS - High frequency updates
    // ============================================================================
    this.socket.on("price:update", (data) => {
      this.updateHeartbeat();
      this.emit("price:update", data);
    });

    this.socket.on("price:subscribed", (data) => {
      console.log("✅ Subscribed to prices:", data);
      this.updateHeartbeat();
      this.emit("price:subscribed", data);
    });

    // ============================================================================
    // CANDLE EVENTS
    // ============================================================================
    this.socket.on('candle:update', (data) => {
      this.updateHeartbeat();
      this.emit('candle:update', data);
    });

    this.socket.on('candle:subscribed', (data) => {
      console.log('✅ Candles subscribed:', data);
      this.updateHeartbeat();
      this.emit('candle:subscribed', data);
    });

    // ============================================================================
    // BROKER-SPECIFIC EVENTS
    // ============================================================================
    this.socket.on("broker:stats", (data) => {
      console.log("📊 Broker stats update:", data);
      this.updateHeartbeat();
      this.emit("broker:stats", data);
    });

    this.socket.on("broker:exposure", (data) => {
      console.log("📊 Broker exposure update:", data);
      this.updateHeartbeat();
      this.emit("broker:exposure", data);
    });

    this.socket.on("broker:alert", (data) => {
      console.warn("🔔 Broker alert:", data.message);
      this.updateHeartbeat();
      this.emit("broker:alert", data);
    });

    // ============================================================================
    // CLIENT ACTIVITY
    // ============================================================================
    this.socket.on("client:login", (data) => {
      console.log("👤 Client logged in:", {
        email: data.email,
        account: data.account_id
      });
      this.updateHeartbeat();
      this.emit("client:login", data);
    });

    this.socket.on("client:logout", (data) => {
      console.log("👤 Client logged out:", {
        email: data.email,
        account: data.account_id
      });
      this.updateHeartbeat();
      this.emit("client:logout", data);
    });

    // ============================================================================
    // NOTIFICATIONS
    // ============================================================================
    this.socket.on("notification", (data) => {
      console.log("🔔 Notification:", data);
      this.updateHeartbeat();
      this.emit("notification", data);
    });

    // ============================================================================
    // ERRORS
    // ============================================================================
    this.socket.on("order:error", (data) => {
      console.error("❌ Order error:", data);
      this.updateHeartbeat();
      this.emit("order:error", data);
    });

    this.socket.on("position:error", (data) => {
      console.error("❌ Position error:", data);
      this.updateHeartbeat();
      this.emit("position:error", data);
    });

    this.socket.on("admin:error", (data) => {
      console.error("❌ Admin error:", data);
      this.updateHeartbeat();
      this.emit("admin:error", data);
    });
  }

  // ============================================================================
  // ADMIN-SPECIFIC METHODS
  // ============================================================================
  requestBrokerStats() {
    if (!this.socket?.connected) {
      console.warn("⚠️ Cannot request stats: Socket not connected");
      return;
    }
    console.log("📊 Requesting broker stats");
    this.socket.emit("admin:get_broker_stats");
  }

  requestExposure() {
    if (!this.socket?.connected) {
      console.warn("⚠️ Cannot request exposure: Socket not connected");
      return;
    }
    console.log("📊 Requesting exposure data");
    this.socket.emit("admin:get_exposure");
  }

  adminClosePosition(accountId, positionId) {
    if (!this.socket?.connected) {
      throw new Error("WebSocket not connected");
    }
    console.log("🔒 Admin closing position:", positionId, "Account:", accountId);
    this.socket.emit("admin:close_position", { accountId, positionId });
  }

  adminCancelOrder(accountId, orderId) {
    if (!this.socket?.connected) {
      throw new Error("WebSocket not connected");
    }
    console.log("🔒 Admin cancelling order:", orderId, "Account:", accountId);
    this.socket.emit("admin:cancel_order", { accountId, orderId });
  }

  triggerMarginCheck(accountId) {
    if (!this.socket?.connected) {
      throw new Error("WebSocket not connected");
    }
    console.log("🔍 Triggering margin check for account:", accountId);
    this.socket.emit("admin:trigger_margin_check", { accountId });
  }

  // ============================================================================
  // PRICE SUBSCRIPTION
  // ============================================================================
  subscribePrices(symbols) {
    if (!this.socket?.connected) {
      console.warn("⚠️ Cannot subscribe: Socket not connected");
      return;
    }

    const newSymbols = symbols.filter(s => !this.subscribedSymbols.has(s));
    if (newSymbols.length === 0) return;

    console.log("📤 Subscribing to prices:", newSymbols);
    newSymbols.forEach(symbol => this.subscribedSymbols.add(symbol));
    this.socket.emit("subscribe:prices", { symbols: newSymbols });
  }

  unsubscribePrices(symbols) {
    if (!this.socket?.connected) return;

    const toUnsubscribe = symbols.filter(s => this.subscribedSymbols.has(s));
    if (toUnsubscribe.length === 0) return;

    console.log("📤 Unsubscribing from prices:", toUnsubscribe);
    toUnsubscribe.forEach(symbol => this.subscribedSymbols.delete(symbol));
    this.socket.emit("unsubscribe:prices", { symbols: toUnsubscribe });
  }

  getSubscribedSymbols() {
    return Array.from(this.subscribedSymbols);
  }

  // ============================================================================
  // CANDLE SUBSCRIPTION
  // ============================================================================
  subscribeCandles(symbol, timeframe) {
    if (!this.socket?.connected) {
      console.warn("⚠️ Cannot subscribe to candles: Socket not connected");
      return;
    }
    console.log(`📊 Subscribing to candles: ${symbol} ${timeframe}`);
    this.socket.emit("candle:subscribe", { symbol, timeframe });
  }

  unsubscribeCandles(symbol, timeframe) {
    if (!this.socket?.connected) return;
    console.log(`📊 Unsubscribing from candles: ${symbol} ${timeframe}`);
    this.socket.emit("candle:unsubscribe", { symbol, timeframe });
  }

  // ============================================================================
  // EVENT EMITTER (Internal)
  // ============================================================================
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
    return () => this.off(event, callback);
  }

  off(event, callback) {
    if (!this.listeners.has(event)) return;
    const callbacks = this.listeners.get(event);
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
    }
  }

  emit(event, data) {
    if (!this.listeners.has(event)) return;
    const callbacks = this.listeners.get(event);
    callbacks.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in ${event} callback:`, error);
      }
    });
  }

  // ============================================================================
  // STATUS & DIAGNOSTICS
  // ============================================================================
  getStatus() {
    return {
      connected: this.isConnected,
      socketId: this.socket?.id,
      transport: this.currentTransport,
      connectionQuality: this.connectionQuality,
      subscribedSymbols: this.getSubscribedSymbols(),
      lastPingReceived: this.lastPingReceived,
      lastHeartbeat: this.lastHeartbeat,
      timeSinceLastPing: Date.now() - this.lastPingReceived,
      timeSinceLastData: Date.now() - this.lastHeartbeat,
      reconnectAttempts: this.reconnectAttempts,
      transportDowngrades: this.transportDowngrades,
    };
  }

  getConnectionQuality() {
    const timeSinceLastPing = Date.now() - this.lastPingReceived;
    const timeSinceLastData = Date.now() - this.lastHeartbeat;
    
    return {
      quality: this.connectionQuality,
      transport: this.currentTransport,
      latency: timeSinceLastPing,
      dataFreshness: timeSinceLastData,
      healthy: this.isConnected && 
               timeSinceLastPing < this.pingTimeout && 
               timeSinceLastData < this.heartbeatTimeout,
    };
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================
  disconnect() {
    if (this.socket) {
      console.log('🔌 Disconnecting broker admin WebSocket');
      this.socket.disconnect();
      this.socket = null;
    }

    this.stopHeartbeatMonitor();
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.listeners.clear();
    this.subscribedSymbols.clear();
    this.token = null;
  }
}

// Create singleton instance
const brokerWsService = new BrokerWebSocketService();

export default brokerWsService;